import { GD } from "src/GD";
import { hasRole } from "src/permissions";
import { assignmentHasNext, assignmentKey, assignmentProjectNames, assignmentRequest, isRecord, parseAssignment, parseAssignments, parseNewAssignmentModel, positiveId, unwrapAssignmentResponse, type Assignment, type AssignmentKey, type AssignmentFilter, type AssignmentPage, type NewAssignmentModel } from "./trafficAssignmentsData";

// Keep the server's field and array decorators for FormParser.
export const loadAssignmentForm = async (projectId: number, key: Pick<AssignmentKey, "rule_id"> | AssignmentKey, signal: AbortSignal) => {
	const response = await GD.REQ_API.request({ ...assignmentRequest("get", projectId, { ...key }), signal });
	signal.throwIfAborted();
	const model = unwrapAssignmentResponse(response);
	if (!isRecord(model) || positiveId(model.rule_id) !== key.rule_id) throw new Error("invalidResponse");
	if ("target_type" in key) {
		const assignment = parseAssignment(model);
		if (!assignment || assignmentKey(assignment) !== assignmentKey(key)) throw new Error("invalidResponse");
	} else parseNewAssignmentModel(model);
	return model;
};

export const loadAssignments = async (projectId: number, ruleId: number, page: number, perPage: number, signal: AbortSignal) => {
	return loadAssignmentPage(projectId, { rule_id: ruleId }, page, perPage, signal);
};
export const loadAssignmentPage = async (projectId: number, filter: AssignmentFilter, page: number, perPage: number, signal: AbortSignal) => {
	const response = await GD.REQ_API.request({ ...assignmentRequest("list", projectId, { ...filter, page, perPage }), signal });
	signal.throwIfAborted();
	const result = parseAssignments(response);
	if (result.rows.some(row => filter.rule_id !== undefined && row.rule_id !== filter.rule_id || filter.target_type !== undefined && row.target_type !== filter.target_type || filter.target_id !== undefined && row.target_id !== filter.target_id)) throw new Error("invalidResponse");
	if (result.rows.some(row => row.target_type === "project" && !row.target_name)) {
		// Missing profile names must not hide otherwise valid assignments.
		const profile = await GD.REQ_PROFILE.request().catch(() => null);
		signal.throwIfAborted();
		return assignmentProjectNames(result, profile?.projects ?? []);
	}
	return result;
};
// Search is local, so collect every page before presenting a complete result set.
export const loadAllAssignments = async (projectId: number, filter: AssignmentFilter, signal: AbortSignal): Promise<AssignmentPage> => {
	const rows: Assignment[] = [], seen = new Set<string>();
	for (let page = 0; ; page++) {
		signal.throwIfAborted();
		const result = await loadAssignmentPage(projectId, filter, page, 100, signal);
		for (const row of result.rows) {
			const key = assignmentKey(row);
			if (seen.has(key)) throw new Error("invalidResponse");
			seen.add(key); rows.push(row);
		}
		if (!assignmentHasNext(result, page, 100)) break;
		if (!result.rows.length) throw new Error("invalidResponse");
	}
	return { rows, total: rows.length, totalPages: Math.ceil(rows.length / 25) };
};
export const loadAssignmentRule = async (projectId: number, assignment: Assignment, signal: AbortSignal) => {
	if (assignment.rule && positiveId(assignment.rule.id) === assignment.rule_id && Array.isArray(assignment.rule.scope)) return assignment.rule;
	const response = await GD.REQ_API.request({ method: "rules/config/get", expectedProjectId: projectId, headers: { ignoreBusy: "true" }, data: { id: assignment.rule_id }, signal });
	signal.throwIfAborted();
	const rule = unwrapAssignmentResponse(response);
	if (!isRecord(rule) || positiveId(rule.id) !== assignment.rule_id || !Array.isArray(rule.scope)) throw new Error("invalidResponse");
	return rule;
};
export const loadAssignment = async (projectId: number, key: AssignmentKey, signal: AbortSignal) => {
	const response = await GD.REQ_API.request({ ...assignmentRequest("get", projectId, { rule_id: key.rule_id, target_type: key.target_type, target_id: key.target_id }), signal });
	signal.throwIfAborted();
	const result = parseAssignment(unwrapAssignmentResponse(response));
	if (!result || assignmentKey(result) !== assignmentKey(key)) throw new Error("invalidResponse");
	return result;
};
export const loadNewAssignmentModel = async (projectId: number, ruleId: number, signal: AbortSignal): Promise<NewAssignmentModel> => {
	return parseNewAssignmentModel(await loadAssignmentForm(projectId, { rule_id: ruleId }, signal));
};
export const mutateAssignment = async (operation: "set" | "remove", assignment: Assignment, projectId: number, userId: number, signal: AbortSignal) => {
	const [profile, project] = await Promise.all([GD.REQ_PROFILE.request(), GD.REQ_CURRENT_PROJECT.request()]);
	if (signal.aborted || profile?.id !== userId || project?.id !== projectId || !hasRole(profile?.projects.find(item => item.id === projectId)?.roles, "admin")) throw new Error("contextChanged");
	const { rule_id, target_type, target_id, enabled, priority, rule_revision, threshold_overrides } = assignment;
	const overrides = threshold_overrides?.map(({ scope_id, use_rule_threshold, value }) => ({ ...(scope_id !== undefined ? { scope_id } : {}), ...(use_rule_threshold !== undefined ? { use_rule_threshold } : {}), ...(value !== undefined ? { value } : {}) }));
	const response = await GD.REQ_API.request({ ...assignmentRequest(operation, projectId, { rule_id, target_type, target_id, ...(operation === "set" ? { enabled, priority, ...(rule_revision !== undefined ? { rule_revision } : {}), ...(overrides !== undefined ? { threshold_overrides: overrides } : {}) } : {}) }), signal });
	signal.throwIfAborted();
	const result = unwrapAssignmentResponse(response);
	if (result === null || result === undefined || result === false) throw new Error("requestError");
	if (operation === "set") {
		const saved = parseAssignment(result);
		if (!saved || assignmentKey(saved) !== assignmentKey(assignment)) throw new Error("invalidResponse");
		return saved;
	}
};
